import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getGlobalData, getStrapiMedia, getPriceMap, getAvailableLocales, getLegalRouteMap, getPageBySlug } from '../lib/strapi';
import {
  DEFAULT_LOCALE,
  ENGLISH_LOCALE,
  SPANISH_VARIANTS,
  isSpanishLocale,
  normalizeLocale,
} from '../lib/locale';
import { getHomeDeeplinkPath, getHomeSlugForLocale } from '../lib/localizedSlugs';

const GlobalContext = createContext();

const getInitialLocale = () => {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const localeFromQuery = params.get('locale');
    if (localeFromQuery) return normalizeLocale(localeFromQuery);

    const stored = window.localStorage.getItem('locale');
    if (stored) return normalizeLocale(stored);
  }

  return normalizeLocale(import.meta.env.VITE_DEFAULT_LOCALE) || DEFAULT_LOCALE;
};

const applyBrandIcons = (companyInfo) => {
  if (!companyInfo) return;

  const icons = companyInfo.browserIcons || {};
  const legacyFavicon = companyInfo.favicon;

  const entries = [
    { rel: 'icon', type: 'image/x-icon', media: icons.faviconIco },
    { rel: 'icon', type: 'image/svg+xml', media: icons.faviconSvg },
    { rel: 'icon', type: 'image/png', sizes: '96x96', media: icons.favicon96 },
    { rel: 'apple-touch-icon', media: icons.appleTouchIcon },
  ].filter((entry) => entry.media?.url);

  if (entries.length === 0 && legacyFavicon?.url) {
    entries.push({ rel: 'icon', type: legacyFavicon.mime, media: legacyFavicon });
  }

  if (entries.length === 0) return;

  document
    .querySelectorAll("link[rel~='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']")
    .forEach((el) => el.parentNode?.removeChild(el));

  entries.forEach(({ rel, type, sizes, media }) => {
    const url = getStrapiMedia(media.url);
    if (!url) return;
    const link = document.createElement('link');
    link.rel = rel;
    if (type) link.type = type;
    if (sizes) link.setAttribute('sizes', sizes);
    link.href = url;
    document.head.appendChild(link);
  });
};

const applyRoundedCorners = (companyInfo) => {
  const enabled = companyInfo?.redondearEsquinas !== false;
  document.documentElement.dataset.roundedCorners = enabled ? 'true' : 'false';
};

const getRgbChannels = (colorValue) => {
  if (!colorValue || typeof document === 'undefined' || typeof window === 'undefined') {
    return null;
  }

  const probe = document.createElement('span');
  probe.style.color = '';
  probe.style.color = colorValue;

  if (!probe.style.color) {
    return null;
  }

  probe.style.display = 'none';
  document.body.appendChild(probe);
  const computedColor = window.getComputedStyle(probe).color;
  probe.remove();

  const channels = computedColor.match(/\d+(?:\.\d+)?/g);
  if (!channels || channels.length < 3) {
    return null;
  }

  return `${channels[0]}, ${channels[1]}, ${channels[2]}`;
};

const applyThemeColors = (theme) => {
  const root = document.documentElement;

  const light = theme?.colors || {};
  const dark = theme?.colorsDark || {};

  if (light.headerFooter) {
    root.style.setProperty('--color-header-footer', light.headerFooter);
    root.style.setProperty('--color-header-bg', light.headerFooter);
    root.style.setProperty('--color-footer-bg', light.headerFooter);
  }
  if (light.menuDesplegable) {
    root.style.setProperty('--color-menu-desplegable', light.menuDesplegable);
    root.style.setProperty('--color-menu-bg', light.menuDesplegable);
  }
  if (light.cta1) {
    root.style.setProperty('--color-cta-1', light.cta1);
    root.style.setProperty('--color-primary', light.cta1);
    root.style.setProperty('--color-btn-primary-bg', light.cta1);

    const cta1Rgb = getRgbChannels(light.cta1);
    if (cta1Rgb) {
      root.style.setProperty('--color-cta-1-rgb', cta1Rgb);
    }
  }
  if (light.cta2) {
    root.style.setProperty('--color-cta-2', light.cta2);
    root.style.setProperty('--color-accent', light.cta2);
    root.style.setProperty('--color-btn-secondary-bg', light.cta2);
  }
  if (light.fondo) {
    root.style.setProperty('--color-fondo', light.fondo);
    root.style.setProperty('--color-background', light.fondo);
  }

  if (dark.headerFooter) {
    root.style.setProperty('--color-header-footer-dark', dark.headerFooter);
    root.style.setProperty('--color-header-bg-dark', dark.headerFooter);
    root.style.setProperty('--color-footer-bg-dark', dark.headerFooter);
  }
  if (dark.menuDesplegable) {
    root.style.setProperty('--color-menu-desplegable-dark', dark.menuDesplegable);
    root.style.setProperty('--color-menu-bg-dark', dark.menuDesplegable);
  }
  if (dark.cta1) {
    root.style.setProperty('--color-cta-1-dark', dark.cta1);
    root.style.setProperty('--color-btn-primary-bg-dark', dark.cta1);
  }
  if (dark.cta2) {
    root.style.setProperty('--color-cta-2-dark', dark.cta2);
    root.style.setProperty('--color-accent-dark', dark.cta2);
  }
  if (dark.fondo) {
    root.style.setProperty('--color-fondo-dark', dark.fondo);
  }
};

export const GlobalProvider = ({ children }) => {
  const [globalData, setGlobalData] = useState({
    companyInfo: null,
    header: null,
    footer: null,
    themeConfig: null,
    priceMap: {},
    legalRouteMap: {},
  });
  const [homeLogoPath, setHomeLogoPath] = useState('');
  const [locale, setLocale] = useState(getInitialLocale);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [headerHidden, setHeaderHidden] = useState(false);
  const [pageOverrides, setPageOverrides] = useState({
    hideHeader: false,
    hideFooter: false,
    hideChatbot: false,
  });
  const [localizedPagePaths, setLocalizedPagePaths] = useState({});
  const [availableLocales, setAvailableLocales] = useState([]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('locale', normalizeLocale(locale));
    }
  }, [locale]);

  // Detect once which locales actually have published content in Strapi
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const locales = await getAvailableLocales();
        if (cancelled) return;
        setAvailableLocales(locales);
        if (locales.length === 0) return;

        // Si el locale guardado no está publicado, buscar reemplazo:
        // - si es español, usar la variante publicada (es ↔ es-MX)
        // - si no, usar DEFAULT_LOCALE si está, sino la primera disponible
        if (!locales.includes(locale)) {
          let fallback = null;
          if (isSpanishLocale(locale)) {
            fallback = locales.find((l) => isSpanishLocale(l)) || null;
          }
          if (!fallback) {
            fallback = locales.includes(DEFAULT_LOCALE) ? DEFAULT_LOCALE : locales[0];
          }
          setLocale(fallback);
        }
      } catch (err) {
        console.warn('Failed to detect available locales:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [locale]);

  useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        setLoading(true);
        const data = await getGlobalData(locale);
        const [priceMap, legalRouteMap, homeLogoPath] = await Promise.all([
          getPriceMap(locale),
          getLegalRouteMap(locale),
          getPageBySlug(getHomeSlugForLocale(locale), locale)
            .then((homePage) => getHomeDeeplinkPath(homePage)),
        ]);
        setGlobalData({ ...data, priceMap, legalRouteMap });
        setHomeLogoPath(homeLogoPath);

        if (data.themeConfig) {
          applyThemeColors(data.themeConfig);
        }
        if (data.companyInfo) {
          applyBrandIcons(data.companyInfo);
          applyRoundedCorners(data.companyInfo);
        }
      } catch (err) {
        console.error('Error loading global data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGlobalData();
  }, [locale]);

  // Escucha el aviso de sync de disponibilidad (serverOrigina) y refresca
  // solo el priceMap (precios "desde" del menu/header), sin recargar nada mas.
  useEffect(() => {
    const serverOriginaUrl = import.meta.env.VITE_SERVER_ORIGINA_URL;
    const wsUrl = serverOriginaUrl ? `${serverOriginaUrl.replace(/^http/, 'ws')}/santafe/ws` : null;
    if (!wsUrl) return undefined;

    let cancelled = false;
    let socket;
    let reconnectTimer;

    const refreshPriceMap = async () => {
      try {
        const freshPriceMap = await getPriceMap(locale);
        if (!cancelled) {
          setGlobalData((prev) => ({ ...prev, priceMap: freshPriceMap }));
          // console.log('[LOG TEMPORAL] priceMap actualizado (Header):', freshPriceMap);
        }
      } catch (err) {
        console.warn('No se pudo refrescar el priceMap tras el sync:', err.message);
      }
    };

    const connect = () => {
      socket = new WebSocket(wsUrl);

      // socket.onopen = () => console.log('[LOG TEMPORAL] WebSocket de sync conectado (GlobalContext/Header)');

      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload?.event === 'sync-completado') {
            // console.log('[LOG TEMPORAL] Aviso de sync recibido, refrescando priceMap...');
            refreshPriceMap();
          }
        } catch {
          // mensaje no reconocido, se ignora
        }
      };

      socket.onclose = () => {
        if (!cancelled) reconnectTimer = setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      socket?.close();
    };
  }, [locale]);

  const changeLocale = useCallback((newLocale) => {
    setLocale(normalizeLocale(newLocale));
  }, []);

  // Códigos reales (los publicados en Strapi) para que Header los use al togglear.
  const spanishLocale = useMemo(() => {
    const fromAvailable = availableLocales.find((l) => isSpanishLocale(l));
    if (fromAvailable) return fromAvailable;
    return SPANISH_VARIANTS.find((v) => v === DEFAULT_LOCALE) || SPANISH_VARIANTS[0];
  }, [availableLocales]);

  const englishLocale = ENGLISH_LOCALE;

  return (
    <GlobalContext.Provider
      value={{
        ...globalData,
        locale,
        changeLocale,
        availableLocales,
        spanishLocale,
        englishLocale,
        loading,
        error,
        headerHidden,
        setHeaderHidden,
        pageOverrides,
        setPageOverrides,
        localizedPagePaths,
        setLocalizedPagePaths,
        homeLogoPath,
        setHomeLogoPath,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error('useGlobal must be used within a GlobalProvider');
  }
  return context;
};

export default GlobalContext;
